import { useEffect, useRef } from 'react'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import {
  computeMetricLeadingPt,
  computeNarrowLeadingPt,
  computeWideLeadingPt,
} from '@/lib/text/leading-presets'

interface LeadingPresetFollowOptions {
  appliedFontSize: { size: number } | null
  leadingChip: 'Narrow' | 'Metric' | 'Wide' | null
  leadingPt: number
  setLeadingPt: (pt: number) => void
  setLeadingSizePt: (pt: number) => void
  setLeadingInputValue: (value: string) => void
  emitStyleUpdate: (update: TextRunStyleUpdate) => void
}

/**
 * A Narrow/Metric/Wide leading preset is defined relative to font size, so it must track font size
 * changes rather than freezing at the pt value computed when the chip was first clicked.
 * Runs only on a size the user applied, never on a resync: the Font Size field reads 14 for a
 * mixed selection, so following it re-applied Narrow at the wrong size.
 *
 * Metric is stored as the size-relative sentinel, so the text already follows the new size; only the
 * displayed pt value goes stale (14.4 beside a 72pt size, WMPCM-11367) and nothing is emitted for it.
 * Narrow/Wide are stored as absolute pt, so they re-emit.
 */
export function useLeadingPresetFollow({
  appliedFontSize,
  leadingChip,
  leadingPt,
  setLeadingPt,
  setLeadingSizePt,
  setLeadingInputValue,
  emitStyleUpdate,
}: LeadingPresetFollowOptions) {
  const followedRef = useRef(appliedFontSize)

  useEffect(() => {
    if (appliedFontSize === followedRef.current) return

    followedRef.current = appliedFontSize

    if (!appliedFontSize) return

    const { size } = appliedFontSize

    setLeadingSizePt(size)

    if (!leadingChip) return

    const pt =
      leadingChip === 'Narrow'
        ? computeNarrowLeadingPt(size)
        : leadingChip === 'Wide'
          ? computeWideLeadingPt(size)
          : computeMetricLeadingPt(size)

    if (pt === leadingPt) return

    setLeadingPt(pt)
    setLeadingInputValue(String(pt))

    if (leadingChip !== 'Metric') emitStyleUpdate({ leading: String(pt) })
  }, [
    appliedFontSize,
    leadingChip,
    leadingPt,
    emitStyleUpdate,
    setLeadingPt,
    setLeadingSizePt,
    setLeadingInputValue,
  ])
}
